import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CONCIERGE_FULL_CSS } from './styles/compiled.css';

interface ShadowDomWrapperProps {
  children: React.ReactNode;
  /**
   * ID for the shadow host element
   */
  hostId?: string;
  /**
   * Theme CSS to inject into the shadow DOM.
   * This CSS is appended after the base compiled CSS and can override default styles.
   * Generated by `generateConciergeCSSFromWidgetTheme()` from bc-widgets theme data.
   */
  themeCSS?: string;
}

// IDs for the style elements in the shadow DOM
const BASE_STYLE_ID = 'concierge-base-styles';
const THEME_STYLE_ID = 'concierge-theme-styles';

/**
 * Wraps children in a Shadow DOM for complete style isolation.
 * Injects pre-compiled Tailwind CSS and handles React event delegation.
 *
 * Theme CSS is kept in a separate style element so it can be updated
 * without re-creating the entire shadow DOM.
 */
export const ShadowDomWrapper: React.FC<ShadowDomWrapperProps> = ({
  children,
  hostId = 'concierge-shadow-host',
  themeCSS,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [mountPoint, setMountPoint] = useState<HTMLDivElement | null>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);

  // Setup shadow DOM and inject base styles (runs once)
  useEffect(() => {
    if (!hostRef.current) return;

    // Check if shadow root already exists (React strict mode / hot reload)
    let shadow = hostRef.current.shadowRoot;

    if (!shadow) {
      shadow = hostRef.current.attachShadow({ mode: 'open' });

      // Inject base compiled Tailwind CSS
      const baseStyle = document.createElement('style');
      baseStyle.id = BASE_STYLE_ID;
      baseStyle.textContent = CONCIERGE_FULL_CSS;
      shadow.appendChild(baseStyle);

      // Create placeholder for theme CSS (will be populated in separate effect)
      const themeStyle = document.createElement('style');
      themeStyle.id = THEME_STYLE_ID;
      shadow.appendChild(themeStyle);

      // Create mount point for React content
      // Note: We don't use display: contents here because we need this element
      // to establish the font-size baseline for all descendants
      const mount = document.createElement('div');
      mount.id = 'concierge-mount';
      shadow.appendChild(mount);

      // Setup event delegation for React
      setupEventDelegation(shadow);
    }

    shadowRef.current = shadow;

    // Get the mount point
    const mount = shadow.getElementById('concierge-mount') as HTMLDivElement;
    setMountPoint(mount);

    return () => {
      // Cleanup is handled automatically when component unmounts
    };
  }, []);

  // Update theme CSS when it changes (separate effect for efficiency)
  useEffect(() => {
    const shadow = shadowRef.current;
    if (!shadow) return;

    const themeStyle = shadow.getElementById(THEME_STYLE_ID) as HTMLStyleElement;
    if (themeStyle) {
      themeStyle.textContent = themeCSS || '';
    }
  }, [themeCSS]);

  return (
    <>
      <div ref={hostRef} id={hostId} style={{ display: 'contents' }} />
      {mountPoint && createPortal(children, mountPoint)}
    </>
  );
};

/**
 * Setup event delegation from Shadow DOM to React's synthetic event system.
 * React 17+ uses the root container for event delegation, but events inside
 * Shadow DOM don't bubble to the document by default.
 */
function setupEventDelegation(shadowRoot: ShadowRoot) {
  // Events that need to be re-dispatched for React
  const delegatedEvents = [
    'click',
    'dblclick',
    'mousedown',
    'mouseup',
    'mouseover',
    'mouseout',
    'mousemove',
    'mouseenter',
    'mouseleave',
    'touchstart',
    'touchend',
    'touchmove',
    'keydown',
    'keyup',
    'keypress',
    'focus',
    'blur',
    'input',
    'change',
    'submit',
    'scroll',
    'wheel',
  ];

  delegatedEvents.forEach((eventType) => {
    shadowRoot.addEventListener(
      eventType,
      () => {
        // Let the event bubble naturally within the shadow tree
        // React handles events through its own synthetic event system
        // which works with the composed path
      },
      { capture: false, passive: eventType === 'scroll' || eventType === 'wheel' },
    );
  });

  // Handle focus events specially - they need to be composed
  shadowRoot.addEventListener('focusin', () => {
    // focusin/focusout are composable by default
  });

  shadowRoot.addEventListener('focusout', () => {
    // focusin/focusout are composable by default
  });
}

/**
 * Custom hook to get the shadow root from within the shadow DOM
 */
export function useShadowRoot(): ShadowRoot | null {
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

  useEffect(() => {
    // Find the shadow host by traversing up the composed path
    const element = document.activeElement;
    if (element) {
      const root = element.getRootNode();
      if (root instanceof ShadowRoot) {
        setShadowRoot(root);
      }
    }
  }, []);

  return shadowRoot;
}

export default ShadowDomWrapper;
